// RetentionPolicyManager.ts
// Manages retention policies for segment storage: RAM capping, age-based pruning,
// quota monitoring, and background compaction.

import { action, makeObservable, observable, runInAction } from 'mobx';
import { segmentIndexStore } from './SegmentIndexStore';

export interface RetentionPolicyConfig {
    // RAM window limits
    maxRamTicks?: number; // Max ticks in RAM (default: 50K)
    maxRamMinutes?: number; // Max time span in RAM (default: 60 min)
    
    // Storage limits
    maxStorageMB?: number; // Max IndexedDB storage (default: 500 MB)
    maxSegmentAgeDays?: number; // Max age for tick segments (default: 365 days)
    maxCandleAgeDays?: number; // Max age for candle segments (default: unlimited)
    
    // Compaction settings
    enableBackgroundCompaction?: boolean; // Auto-compact old segments
    compactionIntervalMs?: number; // How often to check (default: 1 hour)
    compactionAgeDays?: number; // Compact segments older than N days (default: 30)
    
    // Quota management
    quotaWarningThreshold?: number; // Warn at X% of quota (default: 80%)
    quotaEvictionThreshold?: number; // Start eviction at X% (default: 90%)
}

export interface RetentionStats {
    totalSegments: number;
    totalSizeMB: number;
    oldestSegmentAge: number; // Days
    newestSegmentAge: number; // Days
    tickSegmentCount: number;
    candleSegmentCount: number;
    quotaUsagePercent: number;
    lastCompactionTime: number;
    lastPruneTime: number;
}

/**
 * RetentionPolicyManager: enforces data retention policies.
 * Features:
 * - RAM window capping
 * - Age-based segment pruning
 * - IndexedDB quota monitoring
 * - Background compaction
 * - Graceful degradation on quota exceeded
 */
export class RetentionPolicyManager {
    private _config: Required<RetentionPolicyConfig>;
    private _compactionTimer?: ReturnType<typeof setInterval>;
    private _isRunning = false;

    // Observable state
    totalSegments = 0;
    totalSizeMB = 0;
    quotaUsagePercent = 0;
    lastCompactionTime = 0;
    lastPruneTime = 0;
    isCompacting = false;

    constructor(config: RetentionPolicyConfig = {}) {
        this._config = {
            maxRamTicks: config.maxRamTicks ?? 50000,
            maxRamMinutes: config.maxRamMinutes ?? 60,
            maxStorageMB: config.maxStorageMB ?? 500,
            maxSegmentAgeDays: config.maxSegmentAgeDays ?? 365,
            maxCandleAgeDays: config.maxCandleAgeDays ?? 999999, // Unlimited
            enableBackgroundCompaction: config.enableBackgroundCompaction ?? true,
            compactionIntervalMs: config.compactionIntervalMs ?? 60 * 60 * 1000, // 1 hour
            compactionAgeDays: config.compactionAgeDays ?? 30,
            quotaWarningThreshold: config.quotaWarningThreshold ?? 0.8,
            quotaEvictionThreshold: config.quotaEvictionThreshold ?? 0.9,
        };

        makeObservable(this, {
            totalSegments: observable,
            totalSizeMB: observable,
            quotaUsagePercent: observable,
            lastCompactionTime: observable,
            lastPruneTime: observable,
            isCompacting: observable,
            pruneOldSegments: action,
            compactSegments: action,
            enforceQuotaLimit: action,
        });
    }

    /**
     * Start background retention management.
     */
    async start(): Promise<void> {
        if (this._isRunning) return;

        console.log('[RetentionPolicy] Starting retention policy manager');
        this._isRunning = true;

        // Initial cleanup
        await this.pruneOldSegments();
        await this.checkQuota();

        // Start background compaction timer
        if (this._config.enableBackgroundCompaction) {
            this._compactionTimer = setInterval(() => {
                this._backgroundMaintenance();
            }, this._config.compactionIntervalMs);
        }
    }

    /**
     * Stop background retention management.
     */
    stop(): void {
        if (this._compactionTimer) {
            clearInterval(this._compactionTimer);
            this._compactionTimer = undefined;
        }
        this._isRunning = false;
        console.log('[RetentionPolicy] Stopped retention policy manager');
    }

    /**
     * Prune segments older than configured age limits.
     */
    async pruneOldSegments(): Promise<number> {
        console.log('[RetentionPolicy] Starting segment pruning');
        const now = Date.now();
        const segments = Array.from(segmentIndexStore['_index'].values());
        
        let prunedCount = 0;
        const toDelete: string[] = [];

        for (const segment of segments) {
            const ageDays = (now - segment.createdAt) / (1000 * 60 * 60 * 24);
            
            // Check age-based retention
            const maxAge = segment.type === 'tick' 
                ? this._config.maxSegmentAgeDays 
                : this._config.maxCandleAgeDays;
            
            if (ageDays > maxAge) {
                toDelete.push(segment.segmentId);
                console.log('[RetentionPolicy] Marking for deletion:', segment.segmentId, 'age:', ageDays.toFixed(1), 'days');
            }
        }

        // Delete segments
        for (const segmentId of toDelete) {
            await segmentIndexStore.removeSegmentFromIndex(segmentId);
            prunedCount++;
        }

        runInAction(() => {
            this.lastPruneTime = Date.now();
            this.totalSegments = segmentIndexStore['_index'].size;
        });

        console.log('[RetentionPolicy] Pruned', prunedCount, 'old segments');
        return prunedCount;
    }

    /**
     * Compact old segments (re-encode with delta compression).
     */
    async compactSegments(): Promise<number> {
        if (this.isCompacting) {
            console.warn('[RetentionPolicy] Compaction already in progress');
            return 0;
        }

        runInAction(() => { this.isCompacting = true; });
        console.log('[RetentionPolicy] Starting segment compaction');

        const now = Date.now();
        const segments = Array.from(segmentIndexStore['_index'].values());
        const compactionAgeDays = this._config.compactionAgeDays;
        
        let compactedCount = 0;

        for (const entry of segments) {
            // Only compact tick segments
            if (entry.type !== 'tick') continue;
            
            // Only compact if not already compressed
            if (entry.compression === 'delta') continue;
            
            // Only compact if old enough
            const ageDays = (now - entry.createdAt) / (1000 * 60 * 60 * 24);
            if (ageDays < compactionAgeDays) continue;

            try {
                // Load segment
                const segment = await segmentIndexStore.loadSegment(entry.segmentId);
                if (!segment || segment.metadata.type !== 'tick') continue;

                const tickSegment = segment as any; // TickSegment
                
                // Apply delta compression
                const { SegmentIngestionPipeline } = await import('./SegmentIngestionPipeline');
                const compressed = SegmentIngestionPipeline.applyDeltaCompression(tickSegment.ticks);
                
                // Calculate size reduction
                const originalSize = entry.sizeBytes;
                const compressedSize = JSON.stringify(compressed).length;
                const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
                
                // Update segment
                tickSegment.ticks = compressed;
                tickSegment.metadata.compression = 'delta';
                tickSegment.metadata.sizeBytes = compressedSize;
                
                // Persist
                await segmentIndexStore.addSegmentToIndex(tickSegment);
                
                compactedCount++;
                console.log('[RetentionPolicy] Compacted:', entry.segmentId, 'reduction:', reduction + '%');
                
            } catch (error) {
                console.error('[RetentionPolicy] Compaction error:', entry.segmentId, error);
            }
        }

        runInAction(() => {
            this.isCompacting = false;
            this.lastCompactionTime = Date.now();
        });

        console.log('[RetentionPolicy] Compacted', compactedCount, 'segments');
        return compactedCount;
    }

    /**
     * Check IndexedDB quota and enforce limits.
     */
    async checkQuota(): Promise<void> {
        if (!navigator.storage || !navigator.storage.estimate) {
            console.warn('[RetentionPolicy] Storage API not available');
            return;
        }

        const estimate = await navigator.storage.estimate();
        const usage = estimate.usage || 0;
        const quota = estimate.quota || 0;
        
        const usageMB = usage / (1024 * 1024);
        const quotaMB = quota / (1024 * 1024);
        const usagePercent = quota > 0 ? (usage / quota) : 0;

        runInAction(() => {
            this.quotaUsagePercent = usagePercent * 100;
            this.totalSizeMB = usageMB;
        });

        console.log('[RetentionPolicy] Quota:', usageMB.toFixed(2), '/', quotaMB.toFixed(2), 'MB', `(${(usagePercent * 100).toFixed(1)}%)`);

        // Check thresholds
        if (usagePercent >= this._config.quotaEvictionThreshold) {
            console.warn('[RetentionPolicy] Quota exceeded, starting eviction');
            await this.enforceQuotaLimit();
        } else if (usagePercent >= this._config.quotaWarningThreshold) {
            console.warn('[RetentionPolicy] Quota warning:', (usagePercent * 100).toFixed(1) + '%');
        }
    }

    /**
     * Enforce quota limit by evicting old segments.
     */
    async enforceQuotaLimit(): Promise<number> {
        console.log('[RetentionPolicy] Enforcing quota limit');
        
        const segments = Array.from(segmentIndexStore['_index'].values());
        
        // Sort by age (oldest first)
        segments.sort((a, b) => a.createdAt - b.createdAt);
        
        let evictedCount = 0;
        const targetUsage = this._config.quotaWarningThreshold;
        
        // Evict oldest segments until below threshold
        for (const segment of segments) {
            // Check quota after each eviction
            const estimate = await navigator.storage.estimate();
            const currentUsage = (estimate.usage || 0) / (estimate.quota || 1);
            
            if (currentUsage < targetUsage) break;
            
            // Keep candle segments, evict tick segments first
            if (segment.type === 'tick') {
                await segmentIndexStore.removeSegmentFromIndex(segment.segmentId);
                evictedCount++;
                console.log('[RetentionPolicy] Evicted (quota):', segment.segmentId);
            }
        }

        console.log('[RetentionPolicy] Evicted', evictedCount, 'segments for quota');
        await this.checkQuota(); // Update stats
        return evictedCount;
    }

    /**
     * Get retention statistics.
     */
    async getStats(): Promise<RetentionStats> {
        const segments = Array.from(segmentIndexStore['_index'].values());
        const now = Date.now();
        
        const tickSegments = segments.filter(s => s.type === 'tick');
        const candleSegments = segments.filter(s => s.type === 'candle');
        
        const ages = segments.map(s => (now - s.createdAt) / (1000 * 60 * 60 * 24));
        const oldestAge = ages.length > 0 ? Math.max(...ages) : 0;
        const newestAge = ages.length > 0 ? Math.min(...ages) : 0;
        
        const totalSize = segments.reduce((sum, s) => sum + s.sizeBytes, 0);
        const totalSizeMB = totalSize / (1024 * 1024);

        return {
            totalSegments: segments.length,
            totalSizeMB,
            oldestSegmentAge: oldestAge,
            newestSegmentAge: newestAge,
            tickSegmentCount: tickSegments.length,
            candleSegmentCount: candleSegments.length,
            quotaUsagePercent: this.quotaUsagePercent,
            lastCompactionTime: this.lastCompactionTime,
            lastPruneTime: this.lastPruneTime,
        };
    }

    /**
     * Manually trigger full maintenance cycle.
     */
    async runMaintenance(): Promise<void> {
        console.log('[RetentionPolicy] Running manual maintenance cycle');
        
        await this.pruneOldSegments();
        await this.compactSegments();
        await this.checkQuota();
        
        console.log('[RetentionPolicy] Maintenance cycle complete');
    }

    // Private methods

    private async _backgroundMaintenance(): Promise<void> {
        console.log('[RetentionPolicy] Running background maintenance');
        
        try {
            // Check quota first
            await this.checkQuota();
            
            // Prune old segments daily
            const hoursSinceLastPrune = (Date.now() - this.lastPruneTime) / (1000 * 60 * 60);
            if (hoursSinceLastPrune >= 24) {
                await this.pruneOldSegments();
            }
            
            // Compact segments weekly
            const hoursSinceLastCompaction = (Date.now() - this.lastCompactionTime) / (1000 * 60 * 60);
            if (hoursSinceLastCompaction >= 24 * 7) {
                await this.compactSegments();
            }
            
        } catch (error) {
            console.error('[RetentionPolicy] Background maintenance error:', error);
        }
    }
}

// Singleton instance
export const retentionPolicyManager = new RetentionPolicyManager();
